import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Group, Modal, Stack, Switch,
  Table, TextInput, Title, Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useDisclosure } from '@mantine/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconEdit, IconPlus } from '@tabler/icons-react'
import { empresaEntrada, type Empresa, type EmpresaEntrada } from '@serkat/shared'
import { ErrorApi, api } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { ruc as fmtRuc, vacio } from '../../compartido/formato'

export function EmpresasPagina() {
  const { usuario } = useSesion()
  const qc = useQueryClient()
  const [editando, setEditando] = useState<Empresa | undefined>()
  const [abierto, modal] = useDisclosure(false)

  const { data: empresas } = useQuery({
    queryKey: ['empresas'],
    queryFn: () => api.get<Empresa[]>('/empresas?soloActivas=false'),
  })

  const form = useForm<EmpresaEntrada>({
    initialValues: {
      codigo: '', razonSocial: '', ruc: '', direccion: '', telefono: '',
      correo: '', urlCodigoEtica: '', correoDenuncias: '',
      activa: true,
    },
    validate: zodResolver(empresaEntrada),
  })

  const guardar = useMutation({
    mutationFn: (v: EmpresaEntrada) =>
      editando ? api.patch(`/empresas/${editando.id}`, v) : api.post('/empresas', v),
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Empresa guardada' })
      void qc.invalidateQueries({ queryKey: ['empresas'] })
      modal.close()
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const puedeEditar = usuario?.rol === 'ADMIN'

  // La dirección sí es necesaria: encabeza la orden impresa. La URL del código
  // de ética y el correo de denuncias son opcionales: si faltan, sus frases
  // desaparecen de la cláusula en lugar de dejar un hueco visible.
  const sinDireccion = (empresas ?? []).filter((e) => e.activa && !e.direccion)

  function abrir(e?: Empresa) {
    setEditando(e)
    form.setValues(
      e
        ? {
            codigo: e.codigo, razonSocial: e.razonSocial, ruc: e.ruc,
            direccion: e.direccion ?? '', telefono: e.telefono ?? '',
            correo: e.correo ?? '', urlCodigoEtica: e.urlCodigoEtica ?? '',
            correoDenuncias: e.correoDenuncias ?? '',
            activa: e.activa,
          }
        : {
            codigo: '', razonSocial: '', ruc: '', direccion: '', telefono: '',
            correo: '', urlCodigoEtica: '', correoDenuncias: '',
            activa: true,
          },
    )
    modal.open()
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Empresas emisoras</Title>
        {puedeEditar && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => abrir()}>
            Nueva empresa
          </Button>
        )}
      </Group>

      {sinDireccion.length > 0 && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          {sinDireccion.length === 1 ? 'Una empresa no tiene' : `${sinDireccion.length} empresas no tienen`}{' '}
          dirección fiscal. Es el dato que encabeza la orden impresa.
        </Alert>
      )}

      <Table.ScrollContainer minWidth={720}>
        <Table withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Razón social</Table.Th>
              <Table.Th>RUC</Table.Th>
              <Table.Th>Dirección</Table.Th>
              <Table.Th w={130}>Estado</Table.Th>
              <Table.Th w={60} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(empresas ?? []).map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td>
                  {e.razonSocial}
                </Table.Td>
                <Table.Td ff="monospace">{fmtRuc(e.ruc)}</Table.Td>
                <Table.Td>{vacio(e.direccion)}</Table.Td>
                <Table.Td>
                  <Badge color={e.activa ? 'teal' : 'gray'} variant="light" size="sm">
                    {e.activa ? 'Activa' : 'Inactiva'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {puedeEditar && (
                    <Tooltip label="Editar">
                      <ActionIcon variant="subtle" onClick={() => abrir(e)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Modal opened={abierto} onClose={modal.close} title={editando ? 'Editar empresa' : 'Nueva empresa'} size="lg">
        <form onSubmit={form.onSubmit((v) => guardar.mutate(v))}>
          <Stack gap="sm">
            <Group grow align="flex-start">
              <TextInput label="Razón social" withAsterisk {...form.getInputProps('razonSocial')} />
              <TextInput label="Código" withAsterisk placeholder="GRAU" {...form.getInputProps('codigo')} />
            </Group>
            <Group grow align="flex-start">
              <TextInput label="RUC" withAsterisk maxLength={11} {...form.getInputProps('ruc')} />
              <TextInput label="Teléfono" {...form.getInputProps('telefono')} />
            </Group>
            <TextInput label="Dirección" {...form.getInputProps('direccion')} />
            <TextInput label="Correo" {...form.getInputProps('correo')} />
            <TextInput
              label="URL del código de ética"
              description="Opcional. Si se deja vacío, la frase no aparece en la cláusula"
              placeholder="https://…"
              {...form.getInputProps('urlCodigoEtica')}
            />
            <TextInput
              label="Correo del canal de denuncias"
              description="Opcional. Si se deja vacío, la frase no aparece en la cláusula"
              {...form.getInputProps('correoDenuncias')}
            />
            <Switch
              label="Activa"
              description="Las inactivas no aparecen al elegir el emisor de un documento"
              {...form.getInputProps('activa', { type: 'checkbox' })}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={modal.close}>Cancelar</Button>
              <Button type="submit" loading={guardar.isPending}>Guardar</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
